import { useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  useBillingInsightsControllerReadQuery,
  useBillingUsageControllerReadQuery,
  type CompanyBillingSummaryDto,
} from '@shared/services/apiService';
import { billingForecast } from '../model/billingForecast';
import { billingDate, formatBillingMoney, formatCreditUnits } from '../model/formatBillingUnits';
export const BillingInsights = ({
  companyId,
  summary,
  onManage,
  busy,
  canManage,
  refreshSignal = 0,
}: {
  companyId: string;
  summary: CompanyBillingSummaryDto;
  onManage: () => void;
  busy: boolean;
  canManage: boolean;
  refreshSignal?: number;
}) => {
  const query = useBillingInsightsControllerReadQuery(
    { companyId },
    { refetchOnMountOrArgChange: true },
  );
  const usage = useBillingUsageControllerReadQuery(
    { companyId },
    { refetchOnMountOrArgChange: true },
  );
  const [profile, setProfile] = useState('');
  const { refetch } = query;
  useEffect(() => {
    if (refreshSignal) void refetch();
  }, [refreshSignal, refetch]);
  const data = query.isError ? undefined : query.currentData;
  const rate = data?.rates.find((r) => r.id === profile) ?? data?.rates[0];
  const scale = summary.plan?.creditScale ?? 0;
  const available = summary.totalAvailableUnits ?? summary.balance?.availableUnits ?? '0';
  const rateUnits = rate
    ? (BigInt(rate.unitsPerMinute) * BigInt(rate.multiplierNumerator) +
        BigInt(rate.multiplierDenominator) -
        1n) /
      BigInt(rate.multiplierDenominator)
    : null;
  const minutes = rate
    ? (BigInt(available) * BigInt(rate.multiplierDenominator)) /
      (BigInt(rate.unitsPerMinute) * BigInt(rate.multiplierNumerator))
    : null;
  const forecast = usage.currentData
    ? billingForecast(usage.currentData, summary.endsAt, available)
    : null;
  return (
    <Stack spacing={1.5} aria-label="Billing overview">
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Box>
          <Typography variant="subtitle2">
            {summary.status === 'expired' ? 'Last service period' : 'This service period'}
          </Typography>
          {query.isFetching && !data && (
            <Typography variant="body2">Loading payment summary…</Typography>
          )}
          {query.isError && (
            <Alert
              severity="warning"
              action={<Button onClick={() => void refetch()}>Retry summary</Button>}>
              Payment summary is unavailable.
            </Alert>
          )}
          {data?.payments.map((p) => (
            <Box key={`${p.currency}:${p.currencyScale}`}>
              <Typography variant="h6">
                {formatBillingMoney(p.totalMinor, p.currency, p.currencyScale)} paid
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Subscription {formatBillingMoney(p.subscriptionMinor, p.currency, p.currencyScale)}{' '}
                · Manual top-ups {formatBillingMoney(p.manualMinor, p.currency, p.currencyScale)} ·
                Auto top-up {formatBillingMoney(p.automaticMinor, p.currency, p.currencyScale)}
              </Typography>
            </Box>
          ))}
          {data && !data.payments.length && (
            <Typography variant="body2" color="text.secondary">
              No confirmed payments for this service period.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Gross confirmed payments for this period, before refunds. Unpaid checkouts are excluded.
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2">Usage & outlook</Typography>
          <Typography variant="body2">
            {usage.currentData
              ? `${formatCreditUnits(usage.currentData.totalUnits, usage.currentData.creditScale)} credits used · ${usage.currentData.calls} settled calls`
              : usage.isError
                ? 'Usage is unavailable.'
                : 'Usage is loading…'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {forecast
              ? `Estimated use through ${billingDate(summary.endsAt)}: ${formatCreditUnits(forecast.projectedUnits, scale)} credits${forecast.shortfallUnits !== '0' ? ` · ${formatCreditUnits(forecast.shortfallUnits, scale)} more may be needed` : ''}.`
              : 'Forecast available after 3 complete days with recorded usage.'}
          </Typography>
          {rate && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1 }}>
              <TextField
                size="small"
                select
                label="Call profile"
                value={rate.id}
                onChange={(e) => setProfile(e.target.value)}
                sx={{ minWidth: 180, maxWidth: '100%' }}>
                {data?.rates.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.profile} · {r.scope}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="body2">
                {formatCreditUnits(rateUnits!.toString(), scale)} credits/min · about{' '}
                {minutes!.toLocaleString('en-GB')} minutes available
              </Typography>
            </Stack>
          )}
          {data && !rate && (
            <Typography variant="body2" color="text.secondary">
              A duration estimate requires an approved call profile rate.
            </Typography>
          )}
          {rate && (
            <Typography variant="caption" color="text.secondary">
              Estimate for this profile only; includes rounding. Starting a call requires{' '}
              {formatCreditUnits(rate.minimumUnits, scale)} credits and available capacity.
            </Typography>
          )}
        </Box>
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}>
        <Box>
          <Typography variant="body2">
            {data?.paymentLast4
              ? `${data.paymentBrand} •••• ${data.paymentLast4}`
              : 'Payment method managed in Stripe'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[data?.billingName, data?.billingEmail, data?.billingCountry]
              .filter(Boolean)
              .join(' · ') || 'Billing contact and tax details are managed securely in Stripe.'}
          </Typography>
          {data?.billingAddress && (
            <Typography variant="caption" component="p" color="text.secondary">
              {data.billingAddress}
            </Typography>
          )}
          {data?.taxStatus && (
            <Typography variant="caption" component="p" color="text.secondary">
              Tax status:{' '}
              {data.taxStatus === 'exempt'
                ? 'Exempt'
                : data.taxStatus === 'reverse'
                  ? 'Reverse charge'
                  : 'Standard'}
            </Typography>
          )}
        </Box>
        {canManage && (
          <Button size="small" disabled={busy} onClick={onManage}>
            Manage payment method & billing details
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
